import React, { useState } from 'react';
import { TicketData, VenueData, TicketGroupData } from '../types';
import { images } from '../../../../assets/images';
import { getOrganizerCurrency } from '../../../../utils/currency';
import { GripVertical } from 'lucide-react';

interface TicketListProps {
  tickets: TicketData[];
  hasMultipleVenues?: boolean;
  venues?: VenueData[];
  hasMultipleTicketGroups?: boolean;
  ticketGroups?: TicketGroupData[];
  onTicketToggle: (ticketId: string, isActive: boolean) => void;
  onEditTicket?: (ticket: TicketData) => void;
  onDeleteTicket?: (ticketId: string) => void;
  onReorderTickets?: (reorderedTickets: TicketData[]) => void;
}

const TicketList: React.FC<TicketListProps> = ({
  tickets,
  hasMultipleVenues = false,
  venues = [],
  hasMultipleTicketGroups = false,
  ticketGroups = [],
  onTicketToggle,
  onEditTicket,
  onDeleteTicket,
  onReorderTickets
}) => {
  const currencySymbol = getOrganizerCurrency().symbol;

  console.log('Rendering TicketList with tickets:', tickets);

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const updatedTickets = [...tickets];
    const [draggedItem] = updatedTickets.splice(draggedIndex, 1);
    updatedTickets.splice(dropIndex, 0, draggedItem);

    const reorderedWithOrder = updatedTickets.map((t, idx) => ({
      ...t,
      ticketOrder: idx + 1
    }));

    setDraggedIndex(null);
    setDragOverIndex(null);

    if (onReorderTickets) {
      onReorderTickets(reorderedWithOrder as any);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Helper function to get venue name by ID
  const getVenueName = (venueId: string) => {
    const venue = venues.find(v => v.venueId === venueId);
    return venue ? venue.venueName : 'Unknown Venue';
  };

  // Helper function to get ticket group name by ID
  const getTicketGroupName = (ticketGroupId: string) => {
    const ticketGroup = ticketGroups.find(tg => Number(tg.id) === Number(ticketGroupId));
    return ticketGroup ? ticketGroup.groupName : 'Unknown Group';
  };
  
  if (tickets.length === 0) {
    return (
      <div className="quick-v2-empty-state ticket-type-item-empty">
        <img src={images.ticket} alt="" />
        <div>You have no tickets yet.</div>
        <p>You have not created a ticket yet. Please click the button above to create your event ticket.</p>
      </div>
    );
  }

  const isDraggable = Boolean(onReorderTickets && tickets.length > 1);

  return (
    <div className="quick-v2-list ticket-type-item-list">
      {tickets.map((ticket, index) => (
        <div
          key={ticket.ticketTypeId || ticket.id}
          className="quick-v2-row-card price-ticket-card"
          draggable={isDraggable}
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDrop={(e) => handleDrop(e, index)}
          onDragEnd={handleDragEnd}
          style={{
            background: draggedIndex === index
              ? "rgba(238, 63, 124, 0.08)"
              : dragOverIndex === index
              ? "rgba(238, 63, 124, 0.12)"
              : undefined,
            border: dragOverIndex === index
              ? "2px dashed #EE3F7C"
              : undefined,
            opacity: draggedIndex === index ? 0.5 : 1,
            transition: "all 0.15s ease",
            cursor: isDraggable ? "grab" : "default"
          }}
        >
          <div className="quick-v2-row-main" style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {isDraggable && (
              <div
                title="Drag to reorder ticket"
                style={{
                  cursor: "grab",
                  color: "var(--v3-text-muted, rgba(255,255,255,0.4))",
                  display: "flex",
                  alignItems: "center",
                  paddingRight: 2,
                }}
              >
                <GripVertical size={18} />
              </div>
            )}
            <div className={`quick-v2-price-tile ${ticket.price === 0 ? 'free' : ''}`}>
              {ticket.price === 0 ? 'FREE' : `${currencySymbol}${ticket.price.toFixed(2)}`}
            </div>

            <div className="quick-v2-row-copy">
              <div className="quick-v2-title-line">
                <span className="quick-v2-row-title">{ticket.ticketType || ticket.name}</span>
                {ticket.isOfferActive && ticket.previousAmount && (
                  <span className="quick-v2-strike-price">
                    {currencySymbol}{ticket.previousAmount.toFixed(2)}
                  </span>
                )}
                {ticket.isOfferActive && (
                  <span className="quick-v2-offer-chip">
                    <i className="fa-solid fa-tag"></i> Offer Active
                  </span>
                )}
              </div>

              <div className="quick-v2-chip-row">
                <span className="quick-v2-chip">
                  {ticket.isGroupTicket || ticket.type === 'group' ? 'Group Ticket' : 'Single Ticket'}
                </span>
                {hasMultipleVenues && ticket.venueId && (
                  <span className="quick-v2-chip">
                    Venue: {getVenueName(ticket.venueId)}
                  </span>
                )}
                {hasMultipleTicketGroups && ticket.groupingId && (
                  <span className="quick-v2-chip">
                    Group: {getTicketGroupName(ticket.groupingId)}
                  </span>
                )}
              </div>

              <div className="quick-v2-row-meta">
                {ticket.description && (
                  <span>{ticket.description} · </span>
                )}
                <span>
                  Max Allowed Count: <strong>
                    {ticket.maxCount === null || ticket.maxCount === undefined || ticket.maxCount === '-1' || ticket.maxCount === ''
                      ? 'Unlimited'
                      : (ticket.isGroupTicket || ticket.type === 'group') && ticket.groupTicketCount
                      ? (parseInt(ticket.maxCount) * parseInt(ticket.groupTicketCount)).toString()
                      : ticket.maxCount}
                  </strong>
                </span>
              </div>
            </div>
          </div>

          <div className="quick-v2-row-actions">
            <button
              type="button"
              className={`v3-toggle ${ticket.ticketStatus === 'active' || ticket.isActive ? 'on' : 'off'}`}
              onClick={() => onTicketToggle(ticket.ticketTypeId || ticket.id || '', ticket.ticketStatus !== 'active')}
            >
              <span className="v3-toggle-knob" />
            </button>

            {onEditTicket && (
              <button
                type="button"
                className="v3-btn-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  onEditTicket(ticket);
                }}
              >
                <i className="fa-solid fa-pen"></i> Edit
              </button>
            )}

            {onDeleteTicket && (
              <button
                type="button"
                className="quick-v2-danger-btn"
                onClick={(e) => {
                  e.preventDefault();
                  onDeleteTicket(ticket.ticketTypeId || ticket.id || '');
                }}
              >
                <i className="fa-solid fa-trash-can"></i> Delete
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TicketList;